import * as PIXI from 'pixi.js';
import { dispatch, state } from '__common/store';
import { ADD_ENDING_POINT, ADD_STARTING_POINT, DELETE_MEASUREMENT, START_NEW_MEASUREMENT } from '__editor/googleMapsRoofsSelector/components/measurementTool/measurementToolActions';

// Pixi v5 kept the last pointer on interaction.eventData, so a measurement
// click still resolved after the pointer had visited other UI. v7 drops that
// plugin; keep the same last-point fallback here.
let lastGlobalPoint: PIXI.Point | null = null;

function measurementLayers(stage: any): any[] {
  if (!stage) {
    return [];
  }
  if (stage.name === 'measurementToolLayer') {
    return [stage];
  }
  const children = stage.children;
  if (!children || !children.length) {
    return [];
  }
  const layers = [];
  for (let i = 0; i < children.length; i += 1) {
    if (children[i] && children[i].name === 'measurementToolLayer') {
      layers.push(children[i]);
    }
  }
  return layers;
}

function getMeasurementInteractiveObjects(displayObject: any): any[] {
  const layers = measurementLayers(displayObject);
  if (!layers.length) {
    return [];
  }

  const measurements: any[] = [];
  const walk = (object: any) => {
    if (!object) {
      return;
    }
    if (object.name === 'measurementToolInteractive') {
      measurements.push(object);
    }

    const children = object.children;
    if (children && children.length) {
      for (let i = 0; i < children.length; i += 1) {
        walk(children[i]);
      }
    }
  };

  try {
    layers.forEach(walk);
  } catch {
    // A redraw may have detached a child mid-walk. Keep the ones already found.
  }
  return measurements;
}

function getEventGlobalPoint(event: any): PIXI.Point | null {
  if (!event) {
    return null;
  }

  // Panels editor passes a synthetic interaction event. PIXI v7 pointer events
  // expose the same point as `global` and no longer set `data`.
  if (event.data && event.data.global) {
    return event.data.global;
  }

  if (event.global && Number.isFinite(event.global.x) && Number.isFinite(event.global.y)) {
    return event.global;
  }

  return null;
}

function resolveGlobalPoint(event: any): PIXI.Point | null {
  const point = getEventGlobalPoint(event);
  if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) {
    lastGlobalPoint = new PIXI.Point(point.x, point.y);
    return lastGlobalPoint;
  }
  return lastGlobalPoint ? new PIXI.Point(lastGlobalPoint.x, lastGlobalPoint.y) : null;
}

function measurementContainsPoint(measurement: any, globalPoint: PIXI.Point) {
  if (!measurement || typeof measurement.containsPoint !== 'function') {
    return false;
  }

  // containsPoint uses the current world transform and does not refresh it.
  if (typeof measurement.getBounds === 'function') {
    measurement.getBounds();
  }

  return measurement.containsPoint(globalPoint);
}

function findSavedMeasurement(stage: PIXI.Container, globalPoint: PIXI.Point) {
  const measurements = getMeasurementInteractiveObjects(stage);

  for (let i = 0; i < measurements.length; i += 1) {
    const measurement = measurements[i];
    try {
      // Only a saved dimension label may capture the click. The in-progress
      // preview used to match here too, and its box is wide enough to cover
      // the endpoint of a short line, so the click that should commit the
      // line reset it.
      if (Number.isInteger(measurement.measurementIndex) && measurementContainsPoint(measurement, globalPoint)) {
        return measurement;
      }
    } catch {
      // The label was removed while Pixi still tracked it. Skip it and place
      // the point instead of aborting the gesture.
    }
  }

  return null;
}

function updateMeasurementHover(stage: PIXI.Container, globalPoint: PIXI.Point) {
  const hitMeasurement = findSavedMeasurement(stage, globalPoint);
  const measurements = getMeasurementInteractiveObjects(stage);

  return measurements.reduce((changed, measurement) => {
    try {
      if (measurement === hitMeasurement && typeof measurement.showDeleteControls === 'function') {
        return measurement.showDeleteControls() || changed;
      }

      if (typeof measurement.hideDeleteControls === 'function') {
        return measurement.hideDeleteControls() || changed;
      }
    } catch {
      // Hover graphics were rebuilt between moves.
    }

    return changed;
  }, false);
}

function toLocalPoint(stage: PIXI.Container, globalPoint: PIXI.Point) {
  try {
    return stage.toLocal(globalPoint);
  } catch {
    return null;
  }
}

export function measurementToolClick(_renderer: PIXI.Renderer, stage: PIXI.Container, event?: any) {
  const globalPoint = resolveGlobalPoint(event);
  if (!globalPoint || !stage) {
    return;
  }

  // Saved labels are not Pixi event targets (that threw after a redraw). A
  // click on one still deletes it, and the in-progress preview is not named
  // so it cannot swallow the click that finishes a short line.
  const hitMeasurement = findSavedMeasurement(stage, globalPoint);
  if (hitMeasurement) {
    dispatch(DELETE_MEASUREMENT(hitMeasurement.measurementIndex));
    window.dispatchEvent(new CustomEvent('REFRESH_MEASUREMENT_TOOL'));
    return;
  }

  const { measurementTool : { enabled, startingPoint, endingPoint } } = state();
  if (!enabled) {
    return;
  }

  const coords = toLocalPoint(stage, globalPoint);
  if (startingPoint) {
    if (coords && Number.isFinite(coords.x) && Number.isFinite(coords.y)) {
      const distance = Math.hypot(coords.x - startingPoint.x, coords.y - startingPoint.y);
      if (distance >= 0.5) {
        dispatch(ADD_ENDING_POINT(coords));
        dispatch(START_NEW_MEASUREMENT());
        return;
      }
    }
    // Master commits from the last move endpoint when the click itself does
    // not carry a usable point (the pointer was on other UI in between).
    if (endingPoint && Math.hypot(endingPoint.x - startingPoint.x, endingPoint.y - startingPoint.y) >= 0.5) {
      dispatch(START_NEW_MEASUREMENT());
    }
    return;
  }

  if (coords && Number.isFinite(coords.x) && Number.isFinite(coords.y)) {
    dispatch(ADD_STARTING_POINT(coords));
  }
}

export function measurementToolMove(renderer: PIXI.Renderer, stage: PIXI.Container, event?: any) {
  const globalPoint = resolveGlobalPoint(event);
  if (!globalPoint || !stage) {
    return;
  }

  const { measurementTool : { enabled, startingPoint } } = state();
  let hoverChanged = false;
  try {
    hoverChanged = updateMeasurementHover(stage, globalPoint);
  } catch {
    hoverChanged = false;
  }
  if (hoverChanged && renderer) {
    try {
      renderer.render(stage);
    } catch {
      // The stage was rebuilt under the pointer. The endpoint below still lands.
    }
  }

  const coords = toLocalPoint(stage, globalPoint);
  if (enabled && startingPoint && coords && Number.isFinite(coords.x) && Number.isFinite(coords.y)) {
    dispatch(ADD_ENDING_POINT(coords));
  }
}
